import { useCallback, useMemo } from 'react';

import { ConstructionCompanyRegistrationForm } from '../../types/company.types';
import { validateConstructionRegistrationForm } from '../../utils/validations';
import {
  useConstructionFormData,
  useConstructionValidation,
  useConstructionActions,
  useConstructionTouchedFields,
} from '../../stores/company-registration.store';
import { useRepresentativeManagement } from '../common/use-representative-management';
import { useFieldValidation } from '../common/use-field-validation';
import { useDepartmentStaffs } from '../common/use-department-staffs';
import { useMarkRequiredFieldsTouched } from '../common/use-mark-required-fields-touched';

import { PermissionAction } from '@/lib/enums';

// Mock data removed - now using real API data

export const useConstructionRegistrationForm = (
  onSubmit: (data: ConstructionCompanyRegistrationForm) => Promise<void>
) => {
  // Get data and actions from global store
  const formData = useConstructionFormData();
  const validationState = useConstructionValidation();
  const touchedFields = useConstructionTouchedFields();
  const {
    updateForm,
    setValidation,
    resetForm: resetGlobalForm,
    setTouchedFields,
  } = useConstructionActions();

  // Common hooks for shared functionality
  const {
    departments,
    departmentsLoading,
    staffsLoading,
    getSalesPersonOptions,
    handleDepartmentChange,
  } = useDepartmentStaffs(PermissionAction.CREATE);

  // Validate form whenever formData changes
  const validation = useMemo(() => {
    const result = validateConstructionRegistrationForm(formData);
    return result;
  }, [formData, touchedFields]);

  // Mark all required fields as touched on form load to show required errors immediately
  // Construction form uses a list of basic required fields
  useMarkRequiredFieldsTouched({
    touchedFields,
    setTouchedFields,
    representatives: formData.representatives,
    fieldsToMark: ['officialName', 'abbreviatedName', 'nameKatakana'],
    useValidationErrors: false,
  });

  // Field update functions
  const updateField = useCallback(
    (
      field: keyof ConstructionCompanyRegistrationForm,
      value: ConstructionCompanyRegistrationForm[keyof ConstructionCompanyRegistrationForm]
    ) => {
      updateForm({ [field]: value });
      // Mark field as touched for immediate validation display
      setTouchedFields(new Set(touchedFields).add(String(field)));
    },
    [updateForm, setTouchedFields, touchedFields]
  );

  // Representative management using common hook
  const { addRepresentative, updateRepresentative, removeRepresentative, canAddRepresentative } =
    useRepresentativeManagement({
      representatives: formData.representatives,
      updateForm,
      touchedFields,
      setTouchedFields,
      maxRepresentatives: 10,
    });

  // Field validation helpers using common hook
  const { getFieldError, isFieldValid } = useFieldValidation({
    errors: validation.errors,
    touchedFields,
  });

  const isSubmitDisabled = !validation.isValid || validationState.isSubmitting;

  // Form submission
  const handleSubmit = useCallback(async (): Promise<{ success: boolean; message: string }> => {
    if (!validation.isValid) {
      // Mark all fields as touched to show all errors on submit attempt
      const allFieldNames = new Set(
        Object.keys(formData).filter(
          (key) => typeof formData[key as keyof ConstructionCompanyRegistrationForm] !== 'object'
        )
      );
      formData.representatives.forEach((_, index) => {
        allFieldNames.add(`representatives.${index}.department`);
        allFieldNames.add(`representatives.${index}.salesPerson`);
      });
      setTouchedFields(allFieldNames);
      setValidation({ ...validationState, errors: validation.errors, isValid: validation.isValid });
      return { success: false, message: '' };
    }

    setValidation({ ...validationState, isSubmitting: true });

    try {
      await onSubmit(formData);
      return {
        success: true,
        message: '登録が完了しました。',
      };
    } catch (error) {
      console.error('Form submission error:', error);
      const message =
        error instanceof Error ? error.message : '登録に失敗しました。もう一度お試しください。';
      setValidation({
        ...validationState,
        errors: [{ field: 'general', message }],
        isValid: false,
      });
      return {
        success: false,
        message: message,
      };
    } finally {
      setValidation({ ...validationState, isSubmitting: false });
    }
  }, [formData, validation, validationState, onSubmit, setValidation, setTouchedFields]);

  const resetForm = useCallback(() => {
    resetGlobalForm();
    setValidation({
      errors: [],
      isValid: false,
      isSubmitting: false,
    });
    setTouchedFields(new Set());
  }, [resetGlobalForm, setValidation, setTouchedFields]);

  return {
    formData,
    validationState: {
      errors: validation.errors,
      isValid: validation.isValid,
      isSubmitting: validationState.isSubmitting,
    },
    updateField,
    addRepresentative,
    removeRepresentative,
    updateRepresentative,
    handleSubmit,
    resetForm,
    getFieldError,
    isFieldValid,
    getSalesPersonOptions,
    canAddRepresentative,
    isSubmitDisabled,
    // Department and staffs properties
    departments,
    departmentsLoading,
    staffsLoading,
    handleDepartmentChange,
  };
};
